import React, { useState } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import Icon from "components/AppIcon";

const RANGES = ["1W", "1M", "3M", "6M", "1Y", "ALL"];

const generateData = (range) => {
  const points = { "1W": 7, "1M": 30, "3M": 90, "6M": 180, "1Y": 365, "ALL": 500 };
  const n = points?.[range] || 30;
  const data = [];
  let portfolio = 85000, benchmark = 85000;
  const now = new Date("2026-02-28");
  for (let i = n; i >= 0; i--) {
    const d = new Date(now);
    d?.setDate(d?.getDate() - i);
    portfolio += (Math.random() - 0.45) * 1200;
    benchmark += (Math.random() - 0.47) * 900;
    data?.push({
      date: d?.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      portfolio: Math.max(portfolio, 50000),
      benchmark: Math.max(benchmark, 50000),
    });
  }
  return data;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border p-3 text-xs" style={{ background: "var(--color-card)" }}>
      <div className="font-caption text-muted-foreground mb-2">{label}</div>
      {payload?.map((p, i) => (
        <div key={i} className="flex items-center gap-2 mb-1">
          <div className="w-2 h-2 rounded-full" style={{ background: p?.color }} />
          <span className="text-muted-foreground capitalize">{p?.name}:</span>
          <span className="font-data font-semibold text-foreground">${p?.value?.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
        </div>
      ))}
    </div>
  );
};

const PerformanceChart = () => {
  const [range, setRange] = useState("1M");
  const data = generateData(range);

  return (
    <div className="rounded-xl border border-border p-4 md:p-6" style={{ background: "var(--color-card)" }}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-2">
          <Icon name="TrendingUp" size={18} color="var(--color-primary)" />
          <h3 className="font-heading text-base font-semibold text-foreground">Portfolio Performance</h3>
        </div>
        <div className="flex items-center gap-1 p-1 rounded-lg" style={{ background: "var(--color-muted)" }}>
          {RANGES?.map(r => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-2 md:px-3 py-1 rounded-md font-caption text-xs transition-all duration-200 ${range === r ? "text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              style={range === r ? { background: "var(--color-primary)", color: "var(--color-primary-foreground)" } : {}}
            >
              {r}
            </button>
          ))}
        </div>
      </div>
      <div className="w-full h-56 md:h-72">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
            <defs>
              <linearGradient id="portfolioGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#00D9FF" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#00D9FF" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="benchmarkGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#FFB800" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#FFB800" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,217,255,0.08)" />
            <XAxis dataKey="date" tick={{ fill: "var(--color-muted-foreground)", fontSize: 10, fontFamily: "Fira Code" }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
            <YAxis tick={{ fill: "var(--color-muted-foreground)", fontSize: 10, fontFamily: "Fira Code" }} tickLine={false} axisLine={false} tickFormatter={v => `$${(v / 1000)?.toFixed(0)}k`} width={48} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontFamily: "Fira Code", fontSize: 11, color: "var(--color-muted-foreground)" }} />
            <Area type="monotone" dataKey="portfolio" name="Portfolio" stroke="#00D9FF" strokeWidth={2} fill="url(#portfolioGrad)" dot={false} activeDot={{ r: 4, fill: "#00D9FF" }} />
            <Area type="monotone" dataKey="benchmark" name="Benchmark" stroke="#FFB800" strokeWidth={1.5} fill="url(#benchmarkGrad)" dot={false} activeDot={{ r: 4, fill: "#FFB800" }} strokeDasharray="4 2" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PerformanceChart;